/**
 * Applying a completed trade to MY dex, exactly once.
 *
 * WHY THIS EXISTS. Agreeing to a proposal now completes it for BOTH trainers
 * (see cloudflare/src/routes/tradeProposals.ts), which means one of the two
 * people is not holding their phone when the trade completes. Dex writes are
 * device-authoritative — the server never writes a dex — so the absent trainer's
 * lift has to be applied by their own client the next time it sees the finished
 * proposal.
 *
 * That makes "next time it sees" the hard part: the completed list is re-read on
 * every visit, so without a memory of what has already been applied, every load
 * would re-run every historical trade. `markTradeCaught` is idempotent so the
 * DEX would survive that, but the user would not — they'd get a toast storm
 * about trades from weeks ago. Hence the applied-ledger.
 *
 * The ledger is deliberately local (this device applied it) rather than server
 * state: the same account on a second device genuinely does need to apply the
 * lift again to its own local dex.
 */
import { markTradeCaught } from "@/lib/dexStateStore";

const LEDGER_KEY = "nexus.tradeDexLift.applied";

/**
 * How many proposal ids to remember. Comfortably more than the 200 the history
 * layer fetches, so an id can never fall out of the ledger while still being
 * visible in the list — which would make it re-apply on every single load, the
 * exact failure the ledger exists to prevent.
 */
const LEDGER_MAX = 500;

export interface CompletedTradeLike {
  id: number;
  fromAccountId: number;
  giveSpeciesId: number;
  getSpeciesId: number;
  status: string;
}

export interface TradeLiftResult {
  /** Proposal ids applied on this run (already-applied ones are not repeated). */
  appliedIds: number[];
  /** Species that were genuinely NEW to the dex — i.e. the actual lift. */
  newSpeciesIds: number[];
}

function readLedger(): Set<number> {
  if (typeof localStorage === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(LEDGER_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : []);
  } catch {
    // A corrupt ledger must not block trading. Treating it as empty re-applies
    // idempotent writes, which is the safe direction to fail.
    return new Set();
  }
}

function writeLedger(ids: Set<number>): void {
  if (typeof localStorage === "undefined") return;
  try {
    // Highest ids are the most recent proposals, so trimming from the low end
    // drops the ones least likely to still be on screen.
    const trimmed = Array.from(ids).sort((a, b) => a - b).slice(-LEDGER_MAX);
    localStorage.setItem(LEDGER_KEY, JSON.stringify(trimmed));
  } catch {
    /* Storage full or blocked — the writes still happened, we just re-apply. */
  }
}

/** Which species did I RECEIVE in this trade? Give/get are the PROPOSER's view. */
export function speciesIReceived(p: CompletedTradeLike, myAccountId: number): number {
  return p.fromAccountId === myAccountId ? p.getSpeciesId : p.giveSpeciesId;
}

/**
 * Apply every not-yet-applied completed trade to the local dex.
 *
 * Only the species I RECEIVED is written. Giving one away changes nothing in my
 * own dex — the copy left my bag, but the species stays caught.
 *
 * Returns what actually changed so the caller can say something once, for the
 * batch, rather than once per trade.
 */
export function applyCompletedTradesToDex(
  proposals: CompletedTradeLike[],
  myAccountId: number | null,
): TradeLiftResult {
  const result: TradeLiftResult = { appliedIds: [], newSpeciesIds: [] };
  // Without knowing which side I am, "what did I receive" is a coin flip — and a
  // wrong guess writes the wrong species into the dex. Do nothing until the
  // session resolves; the next render will have it.
  if (myAccountId == null) return result;

  const ledger = readLedger();
  let changed = false;

  for (const p of proposals) {
    if (p.status !== "completed") continue;
    if (ledger.has(p.id)) continue;
    const speciesId = speciesIReceived(p, myAccountId);
    if (!Number.isFinite(speciesId) || speciesId < 1) continue;
    // A trade is a species-level acquisition, so it lands on MAIN. The store's
    // cascade rules take care of everything MAIN implies.
    if (markTradeCaught("pokemon", speciesId)) result.newSpeciesIds.push(speciesId);
    ledger.add(p.id);
    result.appliedIds.push(p.id);
    changed = true;
  }

  if (changed) writeLedger(ledger);
  return result;
}

